import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  TopBar as MedsTopBar,
  WidgetHeader,
  APIAlert,
  Loader,
} from '@mummssoftware/common-ui';
import { formatDOBFromPim } from '@mummssoftware/utils/formatters';
import { MAINT_STATUS_COMPLETE } from '@mummssoftware/utils/constants';
import { isClearscriptsMaintStatusResponseOK } from '../utils/utils';
import { messages } from '../utils/messages';
import Body from './Body';
import TopBarContent from '../Sections/TopBar/TopBarContent';
import FilterBarContent from '../Sections/FilterBar/FilterBarContent';
import Medications from './Medications';
import { withMedContext } from '../App/MedContext';
import {
  FILTER_MEDS,
  SET_SORT,
  UPDATE_MED,
  RESET_FILTERS,
  ONLY_NEED_APPROVAL,
  SET_ERROR_MODAL,
} from '../App/constants';
import { Modal } from '../Sections';
import PrintLayout from '../Sections/Print/PrintLayout';
import defaults from '../../translations/en.json';
import { withAppContext } from '../App/AppContext';

const styles = (theme) => ({
  paper: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: 'none',
    notifyRowCount: false,
  },
  modalRoot: {
    marginTop: 'var(--safe-area-inset-top)',
    '& > * > *::-webkit-scrollbar-track, & > * > *::-webkit-scrollbar-corner': {
      background: `${theme.palette.background.default}ce`,
    },
    '@media print': {
      overflow: 'visible',
      visibility: 'hidden',
      display: 'none',
    },
  },
  modalRootMobile: {
    '& > * > *::-webkit-scrollbar-track, & > * > *::-webkit-scrollbar-corner': {
      background: theme.palette.background.header,
    },
    [theme.breakpoints.up('sm')]: {
      '@media print': {
        maxWidth: theme.props.isSafari ? 'calc(100% - 3cm)' : null,
      },
    },
  },
  loaderPaper: {
    background: 'transparent',
  },
  errorCell: {
    color: theme.palette.error.main,
  },
  openRoot: {
    height: '100vh',
    width: '100vw',
    top: 0,
    left: 0,
    position: 'absolute',
    touchAction: 'none',
    ...theme.props.printRules.hide,
  },
  pLinkContainer: {
    position: 'relative',
    display: 'inline-block',
    marginLeft: -5,
    minWidth: 24,
    height: 14,
  },
  patientLink: {
    position: 'absolute',
  },
  certHeader: {
    '&:hover $certHeaderIcon': {
      opacity: 1,
    },
  },
  certHeaderIcon: {
    opacity: 0,
    transition: 'opacity 0.2s ease 0s',
  },
  certCellCheckRoot: {
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: 'inherit',
    },
    cursor: 'default',
    color: theme.palette.primary.main,
    padding: 0,
    verticalAlign: 'text-bottom',
  },
  certCellIcon: { fontSize: 20 },
  iconLaunch: {
    fontSize: 18,
  },
});

const LoaderX = <Loader key="nested-loader" />;

class Listing extends React.PureComponent {
  constructor(props) {
    super(props);
    this.prevFrom = false;
    this.prevTo = false;
    this.toSearch = false;
    this.state = {
      open: false,
      expand: !!props.viewMode,
      loaded: true,
      message: {},
      medsError: [],
      showErrorMessage: false,
    };
  }

  static defaultProps = {
    isMobile: false,
  };

  headerRef = React.createRef();

  containerRef = React.createRef();

  printComponentRef = React.createRef();

  handleSearch = (event) => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: 'toSearch',
        paramVal: event.target.value,
      },
    });
  };

  resetFilters = () => {
    this.props.dispatch({ type: RESET_FILTERS });
  };

  expandAll = () => {
    this.setState(
      {
        expand: false,
      },
      () => {
        this.setState({
          expand: true,
        });
      },
    );
  };

  showAll = () => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: 'type',
        paramVal: 'all',
      },
    });
  };

  showActive = () => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: 'type',
        paramVal: 'active',
      },
    });
  };

  showNeedsApproval = () => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: 'type',
        paramVal: 'needsApproval',
      },
    });
  };

  showRecent = () => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: 'type',
        paramVal: 'recent',
      },
    });
  };

  collapseAll = () => {
    this.setState(
      {
        expand: true,
      },
      () => {
        this.setState({
          expand: false,
        });
      },
    );
  };

  handleExternalLink = async () => {
    const { getExternalURL } = this.props;
    this.setState({ loaded: false });
    const response = await getExternalURL();
    if (response.embeddable === 'false' && !response.status) {
      this.setState({
        url: response.url,
      });
      response.message = <FormattedMessage {...messages.finished} />;
      response.url = null;
    }
    this.toggleModal(response);
    this.setState({ loaded: true });
  };

  handleDateChange = (type, value) => {
    this.props.dispatch({
      type: FILTER_MEDS,
      payload: {
        updateParam: type,
        paramVal: value,
      },
    });
  };

  toggleModal = async (response) => {
    const { open, url } = this.state;
    const {
      errorModalOpen,
      dispatch,
      smallWidget,
      maintPayload,
      updateMaintStatusV1,
      updateMaintStatusV2,
      updateMedications,
      isInMedOrdersBinList,
      onClick,
    } = this.props;
    // if error in meds before closing the widget bar
    if (errorModalOpen) {
      dispatch({
        type: SET_ERROR_MODAL,
        payload: false,
      });
    } else {
      if (!open && url) {
        const tab = window.open(url);
        this.setState({
          tab,
        });
      }
      const message = response || {};
      if (open) {
        let updateMaintResp;
        // We still need to support both maint status workflows.
        // Users that are live with Med Orders are using the new workflow
        // updateMaintStatusV2.  Sites not yet live with med orders still
        // need to use the original updateMaintStatusV1 workflow.
        try {
          if (isInMedOrdersBinList) {
            maintPayload.status = MAINT_STATUS_COMPLETE;
            updateMaintResp = await updateMaintStatusV2(maintPayload);
          } else {
            updateMaintResp = await updateMaintStatusV1(MAINT_STATUS_COMPLETE);
          }
          if (!isClearscriptsMaintStatusResponseOK(updateMaintResp)) {
            this.setState({ showErrorMessage: true });
          } else {
            !smallWidget && updateMedications();
          }
        } catch (error) {
          console.error(error);
        }

        const { tab } = this.state;
        tab && tab.close();
      }
      this.setState({
        open: !open,
        message,
      });
      if (open && smallWidget) {
        onClick();
      }
    }
  };

  handleColClick = (cols) => {
    this.props.dispatch({ type: SET_SORT, payload: cols });
  };

  handleMedChange = (medication, key, value, options) => {
    this.props.dispatch({
      type: UPDATE_MED,
      payload: {
        med: medication,
        key,
        value,
        options,
      },
    });
  };

  componentDidMount() {
    // todo better handling of the hard coded id
    const id = this.props.smallWidget
      ? 'meds-small-container'
      : 'meds-large-container';
    const containerWidth = document.getElementById(id).clientWidth;
    this.setState({ containerWidth });
    if (this.props.maintainMed || this.props.showCheckmarks) {
      this.showNeedsApproval();
      this.props.dispatch({
        type: ONLY_NEED_APPROVAL,
        payload: true,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.showCheckmarks !== this.props.showCheckmarks) {
      if (this.props.showCheckmarks === true) {
        this.showNeedsApproval();
      } else {
        this.showAll();
      }
    }
  }

  render() {
    const {
      classes,
      hospiceBin,
      hospiceName,
      isMumms,
      firstName,
      lastName,
      embedded,
      patient,
      doctors,
      hospicePhys,
      siteId,
      medications,
      siteNickname,
      siteList,
      handleAgencyChange,
      handleSiteChange,
      logout,
      handleApproveMeds,
      maintainMed,
      dashboardEmbedded,
      appTitle,
      onClick,
      onRemove,
      doNotShow,
      patientTopBar,
      online,
      smallWidget,
      theme,
      handleMedChange,
      isMobile,
      handleApproveMedsBtnClick,
      updateApprovedMedsHash,
      showCheckmarks,
      cancelApprovalProcess,
      areMedsApproved,
      handleApproveClick,
    } = this.props;
    const {
      expand,
      open,
      message,
      loaded,
      medsError,
      showErrorMessage,
    } = this.state;
    const { errorModalOpen } = this.props;
    const appBarTitle = `${patient?.firstName} ${patient?.lastName} (${
      patient?.patientNumber
    }) - ${
      patient && patient.sex ? patient.sex[0] : undefined
    } - DOB ${formatDOBFromPim(patient?.dateOfBirth)} (${patient?.age})`;
    const offLineTitle = `${patient?.firstName} ${patient?.lastName} (${patient?.patientNumber}) - Offline`;
    // eslint-disable-next-line no-nested-ternary
    const rest = online === false ? offLineTitle : appBarTitle;
    const title = <FormattedMessage {...messages.title} values={{ rest }} />;

    const handleApproveMedsOnly = () => {
      if (maintainMed) {
        this.props.dispatch({
          type: FILTER_MEDS,
          payload: {
            updateParam: 'type',
            paramVal: undefined,
          },
        });
      }
      handleApproveMeds();
    };

    const { formatMessage } = this.props.intl;
    const fullWidgetTitle = appTitle || defaults.widgetTitle;
    const widgetHeaderTitle =
      dashboardEmbedded && !smallWidget && isMobile
        ? formatMessage({
            id: 'widgetTitleShort',
            defaultMessage: defaults.widgetTitleShort,
          })
        : fullWidgetTitle;

    return (
      <div
        id={smallWidget ? 'meds-small-container' : 'meds-large-container'}
        className={this.state.open ? classes.openRoot : ''}
        ref={this.containerRef}
      >
        {showErrorMessage ? (
          <APIAlert
            errorInfo={{
              text: <FormattedMessage {...messages.medOrderError} />,
              severity: 'error',
            }}
            handleClose={() => this.setState({ showErrorMessage: false })}
            persistentError
          />
        ) : null}
        {!loaded ? LoaderX : null}
        {/* show TopBar if not embedded or patientTopBar */}
        {(!embedded || patientTopBar) && (
          <MedsTopBar
            ref={this.headerRef}
            title={patient ? title : defaults.widgetTitle}
            titleInfo={`v${process.env.REACT_APP_VERSION}`}
            firstName={firstName}
            lastName={lastName}
            siteList={siteList}
            position={patientTopBar ? 'relative' : 'fixed'}
            currentAgency={
              /* eslint-disable indent */
              hospiceBin
                ? {
                    hospiceBin,
                    hospiceName,
                  }
                : undefined
            }
            currentSite={{
              id: siteId,
              nickName: siteNickname,
            }}
            enableAgencyPicker={isMumms}
            logout={logout}
            noLogout={patientTopBar}
            onAgencyChange={handleAgencyChange}
            onSiteChange={handleSiteChange}
            style={{ overflow: 'scroll' }}
          />
        )}

        {dashboardEmbedded && (
          <WidgetHeader
            appTitle={widgetHeaderTitle}
            versionInfo={`${defaults.widgetTitle} v${process.env.REACT_APP_CLEARSCRIPTS_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`}
            onClick={onClick}
            onRemove={onRemove}
            doNotShow={doNotShow}
            smallWidget={smallWidget}
          >
            <TopBarContent
              handleApproveMeds={handleApproveMedsOnly}
              handleExternalLink={this.handleExternalLink}
              handleMedChange={handleMedChange}
              messages={messages}
              smallWidget={smallWidget}
              resetFilters={this.resetFilters}
              theme={theme}
            />
          </WidgetHeader>
        )}

        <Body
          embedded={embedded}
          dashboardEmbedded={dashboardEmbedded}
          headerRef={this.headerRef}
          smallWidget={smallWidget}
          TopBarContent={
            <TopBarContent
              handleApproveMeds={handleApproveMedsOnly}
              handleExternalLink={this.handleExternalLink}
              handleMedChange={handleMedChange}
              messages={messages}
              theme={theme}
              resetFilters={this.resetFilters}
              smallWidget={smallWidget}
            />
          }
          FilterBarContent={
            <FilterBarContent
              handleApproveMeds={handleApproveMedsOnly}
              handleExternalLink={this.handleExternalLink}
              handleMedChange={handleMedChange}
              messages={messages}
              theme={theme}
              resetFilters={this.resetFilters}
              handleApproveMedsBtnClick={handleApproveMedsBtnClick}
              cancelApprovalProcess={cancelApprovalProcess}
              showCheckmarks={showCheckmarks}
              areMedsApproved={areMedsApproved}
              handleApproveClick={handleApproveClick}
            />
          }
          PrintView={
            <PrintLayout
              ref={(el) => {
                this.printComponentRef = el;
              }}
              medications={medications}
              patient={patient}
              facility={patient.facility}
              hospiceName={patient.hospiceName}
            />
          }
          Medications={
            <Medications
              expand={expand}
              doctors={doctors}
              hospicePhys={hospicePhys}
              handleMedChange={this.handleMedChange}
              containerWidth={this.state.containerWidth}
              patient={patient}
              hospiceName={hospiceName}
              dashboardEmbedded={dashboardEmbedded}
              containerRef={this.containerRef}
              smallWidget={smallWidget}
              updateApprovedMedsHash={updateApprovedMedsHash}
              showCheckmarks={showCheckmarks}
            />
          }
          Dialog={
            <Modal
              open={open || errorModalOpen}
              toggleModal={this.toggleModal}
              message={message}
              error={errorModalOpen}
              medsError={medsError}
              onClick={onClick}
              isMobile={isMobile}
            />
          }
        />
      </div>
    );
  }
}

export default injectIntl(
  withStyles(styles, { withTheme: true })(
    withAppContext(withMedContext(Listing)),
  ),
);
